import {
  resolveCategory,
  type AgeCategory,
  type Discipline,
  type DisciplineCategory,
  type FormationSize,
  type Recording,
} from "./queries/rules";
import type { ID } from "./id";

/**
 * Sanity checks over a rule set.
 *
 * Everything here is a *warning*, never a block. Some of these
 * configurations are entirely legitimate — SENIOR and RODIČOVSKÝ TÝM are
 * both 18+, so their ranges overlap by design — and the backend
 * deliberately accepts them. What the warnings catch is the other case:
 * the same shape produced by a typo, where a competitor would land in
 * two categories at once or in none at all.
 */

/** One half-open axis band, with infinities standing in for "no bound". */
interface Band {
  label: string;
  min: number;
  max: number;
}

const overlap = (a: Band, b: Band) => a.min <= b.max && b.min <= a.max;

/** Pairs of bands that cover a common value. */
function overlapWarnings(bands: Band[], describe: (a: string, b: string) => string): string[] {
  const out: string[] = [];
  for (let i = 0; i < bands.length; i++) {
    for (let j = i + 1; j < bands.length; j++) {
      if (overlap(bands[i], bands[j])) out.push(describe(bands[i].label, bands[j].label));
    }
  }
  return out;
}

/**
 * Values no band covers. Walks the bands in order tracking how far the
 * ladder reaches, so overlapping bands do not fake a gap.
 */
function gapWarnings(bands: Band[], describe: (from: number, to: number) => string): string[] {
  const out: string[] = [];
  const sorted = [...bands].sort((a, b) => a.min - b.min || a.max - b.max);
  let covered = Number.NEGATIVE_INFINITY;
  for (const band of sorted) {
    if (Number.isFinite(covered) && Number.isFinite(band.min) && band.min > covered + 1) {
      out.push(describe(covered + 1, band.min - 1));
    }
    covered = Math.max(covered, band.max);
  }
  return out;
}

function ageBands(categories: AgeCategory[]): Band[] {
  return categories.map((c) => ({
    label: c.name,
    min: c.min_age ?? Number.NEGATIVE_INFINITY,
    max: c.max_age ?? Number.POSITIVE_INFINITY,
  }));
}

/** Checks one age ladder. */
export function checkAgeCategories(categories: AgeCategory[]): string[] {
  const bands = ageBands(categories);
  return [
    ...overlapWarnings(
      bands,
      (a, b) => `Věkové kategorie ${a} a ${b} se překrývají; soutěžící může spadat do obou.`,
    ),
    ...gapWarnings(bands, (from, to) =>
      from === to
        ? `Věk ${from} let nespadá do žádné kategorie.`
        : `Věk ${from}–${to} let nespadá do žádné kategorie.`,
    ),
    ...categories
      .filter((c) => c.min_age !== undefined && c.max_age !== undefined && c.min_age > c.max_age)
      .map((c) => `Kategorie ${c.name} má dolní hranici vyšší než horní.`),
  ];
}

/** Checks one formation ladder. */
export function checkFormationSizes(sizes: FormationSize[]): string[] {
  const bands: Band[] = sizes.map((s) => ({
    label: s.name,
    min: s.min_count,
    max: s.max_count ?? Number.POSITIVE_INFINITY,
  }));
  return [
    ...overlapWarnings(
      bands,
      (a, b) =>
        `Formace ${a} a ${b} se překrývají počtem soutěžících; skupina soutěžících může spadat do obou.`,
    ),
    ...gapWarnings(bands, (from, to) =>
      from === to
        ? `Skupina o ${from} ${from === 1 ? "soutěžícím" : "soutěžících"} nespadá do žádné formace.`
        : `Skupiny o ${from}–${to} soutěžících nespadají do žádné formace.`,
    ),
  ];
}

/**
 * What can be wrong with a discipline's matrix.
 *
 * The kinds are enumerated rather than baked into a sentence so a list
 * of disciplines can roll them up — twenty-one disciplines each missing
 * a scoring sheet is one fact worth stating once, not twenty-one
 * identical lines.
 */
export type DisciplineIssueKind =
  | "no_combos"
  | "none_enabled"
  | "no_time"
  | "no_sheet"
  | "no_deductions"
  | "no_federation_music";

export interface DisciplineIssue {
  kind: DisciplineIssueKind;
  /**
   * Enabled combinations affected. Zero for the two kinds that are
   * about the discipline as a whole, where there is nothing to count.
   */
  count: number;
}

/**
 * Checks a discipline's matrix. Only enabled combinations are examined —
 * a disabled one is not competed, so an unset value on it is not a
 * problem. Unresolved values on an enabled combination are: Modul 8 will
 * need a time limit and a sheet to judge against.
 */
export function disciplineIssues(
  discipline: Discipline,
  /**
   * The ids of the official ČNSMS recordings the rule set holds. Only
   * consulted for `music_source: "federation"`, and passed in rather
   * than read off the discipline because the recordings are rule-set
   * rows, not a property of the discipline.
   *
   * Required, not defaulted: a default would either invent a warning in
   * every caller that forgets, or silence a real one.
   */
  recordingIds: ReadonlySet<ID>,
): DisciplineIssue[] {
  const federation = discipline.music_source === "federation";
  const hasRecording = (id: ID | undefined) => id !== undefined && recordingIds.has(id);

  const combos = discipline.categories ?? [];
  const enabled = combos.filter((c) => c.enabled);

  // Reported even when the matrix is empty: a federation discipline with
  // no default track is unfinished whatever its combinations say, and
  // this is the warning that stops an export on the morning of an event.
  if (combos.length === 0 || enabled.length === 0) {
    const musicIssues: DisciplineIssue[] =
      federation && !hasRecording(discipline.default_recording_id)
        ? [{ kind: "no_federation_music", count: 0 }]
        : [];
    if (combos.length === 0) return [...musicIssues, { kind: "no_combos", count: 0 }];
    return [...musicIssues, { kind: "none_enabled", count: 0 }];
  }

  // Resolved per combination, because that is how the export resolves
  // it: TD Twirl's tým combinations override the discipline's track, so
  // "the discipline has a recording" is the wrong question — each
  // enabled combination has to arrive at one.
  const musicIssues: DisciplineIssue[] = [];
  if (federation) {
    const without = enabled.filter(
      (c) => !hasRecording(resolveCategory(discipline, c).recording_id),
    ).length;
    if (without > 0) musicIssues.push({ kind: "no_federation_music", count: without });
  }

  const missing = (predicate: (c: DisciplineCategory) => boolean) =>
    enabled.filter(predicate).length;

  const counts: [DisciplineIssueKind, number][] = [
    ["no_time", missing((c) => resolveCategory(discipline, c).time_max_seconds === null)],
    ["no_sheet", missing((c) => resolveCategory(discipline, c).scoring_sheet_id === undefined)],
    [
      "no_deductions",
      missing((c) => resolveCategory(discipline, c).deduction_table_id === undefined),
    ],
  ];
  return [
    ...musicIssues,
    ...counts.filter(([, n]) => n > 0).map(([kind, count]) => ({ kind, count })),
  ];
}

/** One issue as a sentence, for the discipline's own page. */
export function describeIssue(issue: DisciplineIssue): string {
  switch (issue.kind) {
    case "no_combos":
      return "Disciplína nemá žádné kombinace: chybí řádky v některé z jejích tabulek.";
    case "none_enabled":
      return "Žádná kombinace není povolená, disciplína se zatím nedá soutěžit.";
    case "no_federation_music":
      // Named here rather than left to the competition, because that is
      // where it otherwise surfaces: the export refuses on the morning
      // of the event, and by then nobody can supply the recording.
      //
      // Zero means the discipline has no default at all and has no
      // matrix to count against; anything else counts the enabled
      // combinations that resolve to nothing.
      return issue.count === 0
        ? "Disciplína se soutěží na hudbu ČNSMS, ale nemá vybranou žádnou nahrávku. Export pro zvukaře se bez ní neobejde."
        : plural(
            issue.count,
            "povolená kombinace nemá vybranou hudbu ČNSMS",
            "povolené kombinace nemají vybranou hudbu ČNSMS",
            "povolených kombinací nemá vybranou hudbu ČNSMS",
          );
    case "no_time":
      return plural(
        issue.count,
        "povolená kombinace nemá určený časový limit",
        "povolené kombinace nemají určený časový limit",
        "povolených kombinací nemá určený časový limit",
      );
    case "no_sheet":
      return plural(
        issue.count,
        "povolená kombinace nemá hodnoticí tabulku",
        "povolené kombinace nemají hodnoticí tabulku",
        "povolených kombinací nemá hodnoticí tabulku",
      );
    case "no_deductions":
      return plural(
        issue.count,
        "povolená kombinace nemá srážkovou tabulku",
        "povolené kombinace nemají srážkovou tabulku",
        "povolených kombinací nemá srážkovou tabulku",
      );
  }
}

/** Checks one discipline, as sentences. */
export function checkDiscipline(
  discipline: Discipline,
  recordingIds: ReadonlySet<ID>,
): string[] {
  return disciplineIssues(discipline, recordingIds).map(describeIssue);
}

/** The order issues are reported in, on both surfaces. */
const ISSUE_ORDER: DisciplineIssueKind[] = [
  "no_federation_music",
  "no_combos",
  "none_enabled",
  "no_time",
  "no_sheet",
  "no_deductions",
];

const WHAT_IS_MISSING: Record<DisciplineIssueKind, string> = {
  no_federation_music: "Chybí hudba ČNSMS",
  no_combos: "Bez kombinací",
  none_enabled: "Bez povolené kombinace",
  no_time: "Chybí časový limit",
  no_sheet: "Chybí hodnoticí tabulka",
  no_deductions: "Chybí srážková tabulka",
};

/**
 * Rolls every discipline's issues into one line per kind, for the
 * disciplines list. Each line says how many disciplines are affected and
 * how many combinations that adds up to, which is the pair that tells
 * you whether it is one broken discipline or the whole set.
 */
export function summarizeDisciplineIssues(
  disciplines: Discipline[],
  /** The ids of the official ČNSMS recordings the rule set holds. */
  recordingIds: ReadonlySet<ID>,
): string[] {
  const totals = new Map<DisciplineIssueKind, { disciplines: number; combos: number }>();
  for (const discipline of disciplines) {
    for (const issue of disciplineIssues(discipline, recordingIds)) {
      const entry = totals.get(issue.kind) ?? { disciplines: 0, combos: 0 };
      entry.disciplines += 1;
      entry.combos += issue.count;
      totals.set(issue.kind, entry);
    }
  }
  const out: string[] = [];
  for (const kind of ISSUE_ORDER) {
    const entry = totals.get(kind);
    if (!entry) continue;
    const where = `${entry.disciplines} ${czechDisciplines(entry.disciplines)}`;
    // The two whole-discipline kinds have no combination count to give.
    out.push(
      entry.combos === 0
        ? `${WHAT_IS_MISSING[kind]}: ${where}.`
        : `${WHAT_IS_MISSING[kind]}: ${where}, ${entry.combos} ${czechCombos(entry.combos)}.`,
    );
  }
  return out;
}

/** Czech has three plural forms: 1, 2–4, and 5+. */
function czechForm(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n < 5) return few;
  return many;
}

const czechDisciplines = (n: number) => czechForm(n, "disciplína", "disciplíny", "disciplín");
const czechCombos = (n: number) => czechForm(n, "kombinace", "kombinace", "kombinací");

function plural(n: number, one: string, few: string, many: string): string {
  return `${n} ${czechForm(n, one, few, many)}.`;
}

/**
 * An official ČNSMS recording that does not clear the rule set's own
 * quality floor.
 *
 * Deliberately NOT a `MusicWarningKind`. That union is a contract with
 * internal/service/music and describes a CLUB's upload — a file whose
 * owner can replace it. These are the federation's own tracks, and the
 * only person who can act is the admin editing this rule set, so the
 * finding must not reach `Annotation.Warnings` or any entry surface. A
 * club told its ČNSMS track is low quality can do nothing but forward the
 * message.
 *
 * Guarded by e2e/specs/hudba/contract.spec.ts, which asserts these kinds
 * stay out of the club-facing union.
 */
export interface RecordingQualityIssue {
  recording: Recording;
  lowBitrate: boolean;
  lowSampleRate: boolean;
}

/**
 * Which of a rule set's recordings fall below its own floors.
 *
 * A floor of zero is "no floor" and checks nothing, the same reading
 * `internal/service/music` applies to an entry upload. A measurement of
 * zero means the file was never measured, which is a different problem
 * and not this one.
 */
export function recordingQualityIssues(
  recordings: Recording[],
  minBitrateKbps: number,
  minSampleRateHz: number,
): RecordingQualityIssue[] {
  const out: RecordingQualityIssue[] = [];
  for (const recording of recordings) {
    const lowBitrate =
      minBitrateKbps > 0 && recording.bitrate_kbps > 0 && recording.bitrate_kbps < minBitrateKbps;
    const lowSampleRate =
      minSampleRateHz > 0 &&
      recording.sample_rate_hz > 0 &&
      recording.sample_rate_hz < minSampleRateHz;
    if (lowBitrate || lowSampleRate) out.push({ recording, lowBitrate, lowSampleRate });
  }
  return out;
}

/**
 * The rollup for the recordings tab, in the shape `RuleChecks` takes.
 *
 * One line for the lot rather than one per file: the fix is the same
 * every time (re-upload a better master, or lower the floor), and a
 * twenty-line list of identical sentences buries that.
 */
export function summarizeRecordingQuality(issues: RecordingQualityIssue[]): string[] {
  if (issues.length === 0) return [];
  const names = issues.map((i) => i.recording.name).join(", ");
  return [
    `Špatná kvalita nahrávky: ${issues.length} ` +
      `${czechForm(issues.length, "nahrávka nesplňuje", "nahrávky nesplňují", "nahrávek nesplňuje")}` +
      ` kvalitu, kterou tato sada pravidel vyžaduje (${names}).`,
  ];
}
